/**
 * PanelRadioTemplatesV1 — gestion des templates de messages radio ANTARES
 * Table : game_radio_message_template
 * Accès : CM (rôle 1) + Référent Scénariste
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type Phase =
  | 'depart'
  | 'arrivee_statut'
  | 'ambiance_cos'
  | 'en_cours_1'
  | 'en_cours_2'
  | 'bilan_samu'
  | 'fin'
  | 'retour_statut';

type TemplateStatus = 'actif' | 'en_creation';

interface RadioTemplate {
  id: number;
  phase: Phase;
  message_template: string;
  statut_radio: number | null;
  service_id: number | null;
  incident_type_id: number | null;
  ordre: number;
  status: TemplateStatus;
  created_at?: string;
  updated_at?: string;
}

interface FormState {
  phase: Phase;
  message_template: string;
  statut_radio: string;
  service_id: string;
  incident_type_id: string;
  ordre: string;
  status: TemplateStatus;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const PHASES: { value: Phase; label: string }[] = [
  { value: 'depart',        label: 'Départ (statut 2)' },
  { value: 'arrivee_statut', label: 'Arrivée sur place (statut 3)' },
  { value: 'ambiance_cos',  label: 'Ambiance COS (message narratif)' },
  { value: 'en_cours_1',    label: 'En cours — situation 1' },
  { value: 'en_cours_2',    label: 'En cours — situation 2' },
  { value: 'bilan_samu',    label: 'Bilan SAMU' },
  { value: 'fin',           label: 'Fin d\'intervention' },
  { value: 'retour_statut', label: 'Retour / disponible (statut 7)' },
];

const PHASE_LABELS: Record<Phase, string> = Object.fromEntries(
  PHASES.map((p) => [p.value, p.label])
) as Record<Phase, string>;

const VARIABLES_HELP = `Variables disponibles dans le template :
{indicatif}        — indicatif radio du véhicule
{grade}            — grade du chef (COS ou équipier)
{nom}              — nom du chef
{prenom}           — prénom du chef
{adresse}          — adresse de l'intervention
{dep}              — code département (ex: CODIS75)
{type_incident}    — type d'incident
{heure}            — heure actuelle HH:MM`;

const EMPTY_FORM: FormState = {
  phase: 'depart',
  message_template: '',
  statut_radio: '',
  service_id: '',
  incident_type_id: '',
  ordre: '0',
  status: 'en_creation',
};

const inputCls =
  'w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60';
const labelCls = 'block text-xs font-medium text-slate-400 mb-1.5';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function authHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

function statusBadge(s: TemplateStatus) {
  if (s === 'actif') return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
      Actif
    </span>
  );
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40">
      En création
    </span>
  );
}

function statutRadioBadge(s: number | null) {
  if (s === null) return <span className="text-slate-500 text-xs">—</span>;
  const colors: Record<number, string> = {
    2: 'text-amber-300',
    3: 'text-emerald-300',
    4: 'text-cyan-300',
    7: 'text-emerald-400',
  };
  return (
    <span className={`font-mono text-xs font-bold ${colors[s] ?? 'text-slate-300'}`}>
      S{s}
    </span>
  );
}

// ─── Composant principal ───────────────────────────────────────────────────────

interface PanelRadioTemplatesV1Props {
  embedded?: boolean;
}

const PanelRadioTemplatesV1: React.FC<PanelRadioTemplatesV1Props> = ({ embedded = false }) => {
  const [templates, setTemplates] = useState<RadioTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [filterPhase, setFilterPhase] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const api = getApiBaseUrl();

  // ── Chargement ──────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (filterPhase)  params.set('phase',  filterPhase);
      if (filterStatus) params.set('status', filterStatus);
      const r = await fetch(`${api}/api/panel/radio-templates?${params}`, { headers: authHeaders() });
      const json = await r.json();
      if (!json.success) throw new Error(json.error || 'Erreur chargement');
      setTemplates(json.data || []);
    } catch (e: any) {
      setError(e?.message || 'Erreur réseau');
    }
    setLoading(false);
  }, [api, filterPhase, filterStatus]);

  useEffect(() => { void load(); }, [load]);

  // ── Formulaire ──────────────────────────────────────────────────────────────

  function startCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSuccess(null);
    setError(null);
  }

  function startEdit(t: RadioTemplate) {
    setEditingId(t.id);
    setForm({
      phase:            t.phase,
      message_template: t.message_template,
      statut_radio:     t.statut_radio != null ? String(t.statut_radio) : '',
      service_id:       t.service_id   != null ? String(t.service_id)   : '',
      incident_type_id: t.incident_type_id != null ? String(t.incident_type_id) : '',
      ordre:            String(t.ordre),
      status:           t.status,
    });
    setSuccess(null);
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  async function handleSave() {
    if (!form.message_template.trim()) {
      setError('Le message template est obligatoire.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    const payload = {
      phase:            form.phase,
      message_template: form.message_template.trim(),
      statut_radio:     form.statut_radio     !== '' ? parseInt(form.statut_radio, 10)     : null,
      service_id:       form.service_id       !== '' ? parseInt(form.service_id, 10)       : null,
      incident_type_id: form.incident_type_id !== '' ? parseInt(form.incident_type_id, 10) : null,
      ordre:            parseInt(form.ordre, 10) || 0,
      status:           form.status,
    };
    try {
      const url    = editingId !== null ? `${api}/api/panel/radio-templates/${editingId}` : `${api}/api/panel/radio-templates`;
      const method = editingId !== null ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const json = await r.json();
      if (!json.success) throw new Error(json.error || 'Erreur sauvegarde');
      setSuccess(editingId !== null ? 'Template mis à jour.' : 'Template créé.');
      cancelEdit();
      void load();
    } catch (e: any) {
      setError(e?.message || 'Erreur réseau');
    }
    setSaving(false);
  }

  async function handleDelete(id: number) {
    try {
      const r = await fetch(`${api}/api/panel/radio-templates/${id}`, { method: 'DELETE', headers: authHeaders() });
      const json = await r.json();
      if (!json.success) throw new Error(json.error || 'Erreur suppression');
      setSuccess('Template supprimé.');
      setDeleteConfirm(null);
      void load();
    } catch (e: any) {
      setError(e?.message || 'Erreur réseau');
    }
  }

  // ── Rendu ────────────────────────────────────────────────────────────────────

  const formPanel = (
    <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-200">
          {editingId !== null ? `Modifier le template #${editingId}` : 'Créer un nouveau template'}
        </h2>
        <button
          type="button"
          onClick={() => setShowHelp((h) => !h)}
          className="text-[10px] text-slate-400 hover:text-slate-200 underline underline-offset-2"
        >
          {showHelp ? 'Masquer les variables' : 'Voir les variables disponibles'}
        </button>
      </div>

      {showHelp && (
        <pre className="rounded-lg border border-[#2a3a62] bg-black/40 px-3 py-2 text-[10px] text-slate-400 whitespace-pre-wrap">
          {VARIABLES_HELP}
        </pre>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Phase *</label>
          <select
            value={form.phase}
            onChange={(e) => setForm((f) => ({ ...f, phase: e.target.value as Phase }))}
            className={inputCls}
          >
            {PHASES.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Statut radio associé</label>
          <select
            value={form.statut_radio}
            onChange={(e) => setForm((f) => ({ ...f, statut_radio: e.target.value }))}
            className={inputCls}
          >
            <option value="">Aucun (message narratif)</option>
            <option value="2">Statut 2 — en route</option>
            <option value="3">Statut 3 — sur place</option>
            <option value="4">Statut 4 — quitte les lieux</option>
            <option value="7">Statut 7 — disponible</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Service (filtre)</label>
          <select
            value={form.service_id}
            onChange={(e) => setForm((f) => ({ ...f, service_id: e.target.value }))}
            className={inputCls}
          >
            <option value="">Tous les services</option>
            <option value="2">Sapeurs-pompiers (SP)</option>
            <option value="3">SAMU / SMUR</option>
            <option value="4">Forces de l'ordre (FDO)</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Incident type ID (filtre)</label>
          <input
            type="number"
            min={0}
            value={form.incident_type_id}
            onChange={(e) => setForm((f) => ({ ...f, incident_type_id: e.target.value }))}
            placeholder="Laisser vide = tous types"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Ordre (tri aléatoire parmi variantes)</label>
          <input
            type="number"
            min={0}
            value={form.ordre}
            onChange={(e) => setForm((f) => ({ ...f, ordre: e.target.value }))}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Statut</label>
          <select
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as TemplateStatus }))}
            className={inputCls}
          >
            <option value="en_creation">En création</option>
            <option value="actif">Actif</option>
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls}>Message template * (utilisez les variables entre accolades)</label>
        <textarea
          rows={4}
          value={form.message_template}
          onChange={(e) => setForm((f) => ({ ...f, message_template: e.target.value }))}
          placeholder="{indicatif}, statut 2, en route pour {adresse}, FIN"
          className={`${inputCls} font-mono resize-y`}
        />
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-medium text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors disabled:opacity-50"
        >
          {saving ? 'Enregistrement…' : editingId !== null ? 'Mettre à jour' : 'Créer le template'}
        </button>
        {editingId !== null && (
          <button
            type="button"
            onClick={cancelEdit}
            className="rounded-lg border border-white/15 bg-black/30 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-black/45 transition-colors"
          >
            Annuler
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-bold text-white">Templates radio ANTARES</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Messages simulés générés automatiquement par RadioJournalService à chaque phase d'intervention.
          </p>
        </div>
        <button
          type="button"
          onClick={startCreate}
          className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nouveau template
        </button>
      </div>

      {error   && <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</div>}
      {success && <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">{success}</div>}

      {formPanel}

      {/* Filtres */}
      <div className="flex gap-3 flex-wrap">
        <select
          value={filterPhase}
          onChange={(e) => setFilterPhase(e.target.value)}
          className="rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-1.5 text-sm text-slate-100 focus:outline-none"
        >
          <option value="">Toutes les phases</option>
          {PHASES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-1.5 text-sm text-slate-100 focus:outline-none"
        >
          <option value="">Tous les statuts</option>
          <option value="actif">Actif</option>
          <option value="en_creation">En création</option>
        </select>
      </div>

      {/* Liste */}
      {loading ? (
        <p className="text-sm text-slate-500">Chargement…</p>
      ) : templates.length === 0 ? (
        <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 px-4 py-8 text-center text-slate-400 text-sm">
          Aucun template trouvé.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#243052]/90">
          <table className="w-full text-left min-w-[900px]">
            <thead className="border-b border-[#1e2d4d]">
              <tr>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">ID</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Phase</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Statut radio</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Service</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Ordre</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Statut</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Message template</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id} className="border-b border-[#1e2d4d]/60 hover:bg-[#0d1b3a]/40 transition-colors">
                  <td className="px-4 py-3 text-slate-400 text-xs">#{t.id}</td>
                  <td className="px-4 py-3 text-slate-200 text-xs whitespace-nowrap">
                    {PHASE_LABELS[t.phase] ?? t.phase}
                  </td>
                  <td className="px-4 py-3">{statutRadioBadge(t.statut_radio)}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {t.service_id === 2 ? 'SP' : t.service_id === 3 ? 'SAMU' : t.service_id === 4 ? 'FDO' : t.service_id != null ? `#${t.service_id}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{t.ordre}</td>
                  <td className="px-4 py-3">{statusBadge(t.status)}</td>
                  <td className="px-4 py-3 text-slate-300 text-xs font-mono max-w-[360px] truncate" title={t.message_template}>
                    {t.message_template}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => startEdit(t)}
                        className="rounded border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-2 py-1 text-[10px] font-medium text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors"
                      >
                        Modifier
                      </button>
                      {deleteConfirm === t.id ? (
                        <>
                          <button
                            type="button"
                            onClick={() => void handleDelete(t.id)}
                            className="rounded border border-red-500/50 bg-red-500/20 px-2 py-1 text-[10px] font-medium text-red-300 hover:bg-red-500/30 transition-colors"
                          >
                            Confirmer
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(null)}
                            className="rounded border border-white/10 bg-black/30 px-2 py-1 text-[10px] text-slate-300 hover:bg-black/45 transition-colors"
                          >
                            Annuler
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(t.id)}
                          className="rounded border border-red-500/35 bg-red-500/10 px-2 py-1 text-[10px] font-medium text-red-300 hover:bg-red-500/20 transition-colors"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PanelRadioTemplatesV1;
